import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import axios from "axios";
import { useApi } from "../../../service/ApiContext";
import { useAlert } from "../../../components/AlertContextComponent";
import InterviewHubApiService from "../service/InterviewHubApiService";
import {
  ResumeOptimizationSession,
  OptimizeResumeStatus,
  JDMatchResponse,
  BulletOptimizationState,
  BulletStatus,
} from "../data/interface/InterviewHubInterface";
import ResumePicker, { ResumeFileData } from "../components/ResumePicker";
import RewriteChatModal from "../components/RewriteChatModal";
import OptimizationInitModal from "../components/OptimizationInitModal";
import Loader from "../../../utils/Utils";
import "../styles/InterviewHubStyles.css";

const POLL_INTERVAL_MS = 4000;
const CIRCUMFERENCE = 2 * Math.PI * 52;

const formatDateTime = (dateString: string) => {
  if (!dateString) return "";
  const normalized = dateString.replace(/\//g, "-");
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const scoreColor = (pct: number) => {
  if (pct >= 85) return "#10b981";
  if (pct >= 70) return "#34d399";
  if (pct >= 50) return "#fbbf24";
  return "#f87171";
};

// ─── Sub-component: Match Score Circle ───────────────────────────────────────
const MatchScoreCircle: React.FC<{ score: number }> = ({ score }) => {
  const color = scoreColor(score);
  const offset = CIRCUMFERENCE * (1 - score / 100);

  return (
    <div className="match-score-container">
      <svg viewBox="0 0 120 120" className="match-score-svg">
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="8"
        />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke={`url(#scoreGradient-${score})`}
          strokeWidth="10"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          style={{
            transition:
              "stroke-dashoffset 1.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        />
        <defs>
          <linearGradient
            id={`scoreGradient-${score}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={color} stopOpacity="0.6" />
          </linearGradient>
        </defs>
      </svg>
      <div className="match-score-label">
        <span className="match-score-number" style={{ color }}>
          {score}
        </span>
        <span className="match-score-unit">% Match</span>
      </div>
    </div>
  );
};

// ─── Sub-component: Polling Screen ───────────────────────────────────────────
const PollingScreen: React.FC = () => (
  <div className="rg-polling-screen">
    <div className="rg-polling-orb">
      <div className="rg-polling-spinner" />
      <span className="rg-polling-icon">✨</span>
    </div>
    <h3 className="rg-polling-title">Optimizing Your Profile</h3>
    <p className="rg-polling-desc">
      JozAI is matching your resume against the job description and crafting
      perfect bullet points. This usually takes 30–60 seconds.
    </p>
    <div className="rg-polling-dots">
      <span />
      <span />
      <span />
    </div>
  </div>
);

// ─── Main Component: ResumeAnalyzingTab ──────────────────────────────────────
type AnalyzeViewState = "list" | "polling" | "result";

const ResumeAnalyzingTab: React.FC<{
  onToggleTabs: (show: boolean) => void;
  showInitModal?: boolean;
  setShowInitModal?: (show: boolean) => void;
}> = ({ onToggleTabs, showInitModal = false, setShowInitModal }) => {
  const api = useApi();
  const { showAlert } = useAlert();
  const service = useMemo(() => new InterviewHubApiService(api), [api]);

  const [view, setView] = useState<AnalyzeViewState>("list");
  const [sessions, setSessions] = useState<ResumeOptimizationSession[]>([]);
  const [activeSession, setActiveSession] =
    useState<ResumeOptimizationSession | null>(null);

  const [selectedBulletId, setSelectedBulletId] = useState<string | null>(null);
  const [showRewriteModal, setShowRewriteModal] = useState(false);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchSessions();
    return () => stopPolling();
  }, []);

  // ── Tab visibility management ──
  useEffect(() => {
    if (view === "polling" || view === "result") {
      onToggleTabs(false);
    } else {
      onToggleTabs(true);
    }
    return () => onToggleTabs(true);
  }, [view, onToggleTabs]);

  // ── Fetch session list ──
  const fetchSessions = async () => {
    try {
      Loader.showLoading();

      const data = await service.listOptimizationSessions();
      setSessions(data.optimization_sessions || []);
    } catch (err) {
      console.error("Failed to fetch sessions", err);
    } finally {
      Loader.dismissLoading();
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback(
    (sessionId: string) => {
      stopPolling();
      const poll = async () => {
        try {
          const res = await service.getResumeOptimizationResult(sessionId);
          if (res.status === OptimizeResumeStatus.COMPLETED) {
            stopPolling();
            setActiveSession(res.resume_optimization);
            setView("result");
            fetchSessions();
          } else if (res.status === OptimizeResumeStatus.FAILED) {
            stopPolling();
            setActiveSession(res.resume_optimization);
            setView("result");
            fetchSessions();
          } else {
            pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        } catch (err) {
          pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      };
      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [service],
  );

  const handleStartOptimization = async (file: ResumeFileData, jd: string) => {
    Loader.showLoading("Initializing analyzer...");
    try {
      const uploadUrlData = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(`/create_upload_url?filename=${encodeURIComponent(file.name)}`);
      const { uploadURL, downloadURL } = uploadUrlData.data;

      await axios.put(uploadURL, file.binary, {
        headers: { "Content-Type": file.type },
      });

      const startRes = await service.startResumeOptimization({
        job_description: jd,
        resume_url: downloadURL,
      });

      setShowInitModal?.(false);
      setView("polling");
      startPolling(startRes.session_id);
    } catch (err: any) {
      console.error("Optimization failed", err);
      const data = err?.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        "Something went wrong while starting the analysis. Please try again.";
      showAlert({
        show: true,
        title: "Error",
        message: msg,
        alertType: "error",
        onlyNeedOneButton: true,
        button1Text: "Got it",
        btn1onPressed() {},
      });
    } finally {
      Loader.dismissLoading();
    }
  };

  const handleViewSession = async (session: ResumeOptimizationSession) => {
    if (session.status === OptimizeResumeStatus.COMPLETED) {
      Loader.showLoading("Loading analysis...");
      try {
        const res = await service.getResumeOptimizationResult(
          session.session_id,
        );
        if (res && res.resume_optimization) {
          setActiveSession(res.resume_optimization);
          setView("result");
        }
      } catch (err) {
        console.error("Failed to load session", err);
      } finally {
        Loader.dismissLoading();
      }
    } else if (session.status === OptimizeResumeStatus.INITIATED) {
      setView("polling");
      startPolling(session.session_id);
    } else if (session.status === OptimizeResumeStatus.FAILED) {
      setActiveSession(session as any);
      setView("result");
    }
  };

  const handleBulletUpdate = (newState: BulletOptimizationState) => {
    if (activeSession) {
      const updatedBulletStates = {
        ...activeSession.bullet_states,
        [newState.bullet_id]: newState,
      };

      const completedCount = Object.values(updatedBulletStates).filter(
        (b) => b.status !== BulletStatus.PROPOSED,
      ).length;

      setActiveSession({
        ...activeSession,
        bullet_states: updatedBulletStates,
        completed_modifications: completedCount,
      });

      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === activeSession.session_id
            ? { ...s, completed_modifications: completedCount }
            : s,
        ),
      );
    }
  };

  // Get score-based styling for optimization cards
  const getScoreStyles = (matchScore?: number) => {
    if (matchScore === undefined) {
      return {
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      };
    }

    if (matchScore >= 81 && matchScore <= 100) {
      return {
        backgroundColor: "#E3FFF5",
        border: "1px solid #24C782",
      };
    } else if (matchScore >= 51 && matchScore <= 80) {
      return {
        backgroundColor: "#FFF8DB",
        border: "1px solid #FFCA64",
      };
    } else if (matchScore >= 0 && matchScore <= 50) {
      return {
        backgroundColor: "#FFEEEE",
        border: "1px solid #E84C3D",
      };
    }

    return {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
    };
  };

  // Truncate text helper
  const truncateText = (text: string, maxLength: number = 35) => {
    if (!text) return "No description";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + "...";
  };

  const activeBulletState = useMemo(() => {
    if (!activeSession || !activeSession.bullet_states || !selectedBulletId)
      return null;
    return activeSession.bullet_states[selectedBulletId];
  }, [activeSession, selectedBulletId]);

  if (view === "polling") return <PollingScreen />;

  if (view === "result" && activeSession) {
    const res = activeSession.result;
    return (
      <div className="opt-result-container animation-fade-in">
        <div className="rg-result-header mb-4">
          <button className="rg-back-btn" onClick={() => setView("list")}>
            ← Back
          </button>
          <div className="rg-result-title-block">
            <h2 className="rg-result-title">Resume Analysis Report</h2>
          </div>
        </div>

        {activeSession.status === OptimizeResumeStatus.FAILED ? (
          <div className="rg-failure-state">
            <div className="rg-failure-icon">⚠️</div>
            <h3 className="rg-failure-title">Analysis Failed</h3>
            <p className="rg-failure-reason">
              {activeSession.reason || "Unknown error."}
            </p>
          </div>
        ) : (
          <div className="rg-result-tab-content">
            <div className="rg-result-inner-container">
              <div className="match-dashboard-grid mt-2">
                {/* Left Column: Match Score & Screening */}
                <div className="match-score-card glass-panel">
                  <MatchScoreCircle
                    score={activeSession.match_score || res?.match_score || 0}
                  />
                  <div className="screening-box mt-3">
                    <span className="text-silver-muted small text-uppercase fw-bold">
                      Screening Probability
                    </span>
                    <div
                      className={`screening-badge ${res?.screening_prediction.toLowerCase()}`}
                    >
                      {res?.screening_prediction || "Maybe"}
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills & Gaps */}
                <div className="skills-analysis-card glass-panel d-flex flex-column gap-4">
                  <div className="d-flex gap-4">
                    <div className="skills-section flex-1">
                      <h6 className="text-blue-bright mb-3">
                        Hard Skills Identified
                      </h6>
                      <div className="skills-group">
                        {res?.hard_skills.map((s, i) => (
                          <span key={i} className="skill-pill">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="skills-section flex-1">
                      <h6 className="text-blue-bright mb-3">
                        Soft Skills Identified
                      </h6>
                      <div className="skills-group">
                        {res?.soft_skills.map((s, i) => (
                          <span key={i} className="skill-pill soft">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="skills-section border-top pt-4">
                    <h6 className="text-blue-bright mb-3">
                      Identified Keyword Gaps
                    </h6>
                    <div className="skills-group mt-2">
                      {res?.missing_keywords.length ? (
                        res.missing_keywords.map((s, i) => (
                          <span key={i} className="skill-pill gap">
                            {s}
                          </span>
                        ))
                      ) : (
                        <span className="text-success small">
                          No major keyword gaps! You&apos;re well-aligned.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategic Modifications */}
              <div className="d-flex justify-content-between align-items-center mt-5 mb-3">
                <h5 className="m-0">✨ Strategic Profile Modifications</h5>
                <div className="progress-badge-v2">
                  Optimization Progress: {activeSession.completed_modifications}
                  /{activeSession.total_modifications}
                </div>
              </div>

              <div className="strategic-mod-grid">
                {res?.strategic_modifications.map((mod, i) => {
                  const bId = `mod_${i}`;
                  const state = activeSession.bullet_states?.[bId];
                  const status = state?.status || BulletStatus.PROPOSED;
                  const isAccepted = status === BulletStatus.ACCEPTED;

                  return (
                    <div
                      key={bId}
                      className={`mod-card glass-panel v2 ${status.toLowerCase()}`}
                    >
                      <div className="mod-card-header">
                        <span className="mod-label">
                          Rewrite Suggestion #{i + 1}
                        </span>
                        {isAccepted ? (
                          <span className="status-tag accepted animate-bounce">
                            Accepted ✨
                          </span>
                        ) : (
                          <span
                            className={`status-tag ${status.toLowerCase()}`}
                          >
                            {status}
                          </span>
                        )}
                      </div>
                      <p className="mod-reasoning small italic mt-2 text-silver-muted">
                        &quot;{mod.reasoning}&quot;
                      </p>
                      <div className="mod-comparison mt-3">
                        <div className="mod-point original">
                          <small className="text-blue-muted text-uppercase fw-bold d-block mb-1">
                            Original Description
                          </small>
                          <span className="text-white-80">
                            {mod.original_point}
                          </span>
                        </div>
                        <div className="mod-point suggestion mt-3 position-relative">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-blue-bright text-uppercase fw-bold m-0">
                              Optimized Result
                            </small>
                            <Button
                              variant="link"
                              className="p-0 text-silver-muted hover-white"
                              style={{ textDecoration: "none" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  state?.ai_suggestion ||
                                    mod.rewrite_suggestion,
                                );
                              }}
                              title="Copy to clipboard"
                            >
                              <i className="fas fa-copy"></i>
                            </Button>
                          </div>
                          <span className="mod-reasoning small italic mt-2 text-silver-muted">
                            {state?.ai_suggestion || mod.rewrite_suggestion}
                          </span>
                        </div>
                      </div>
                      <Button
                        className={`mod-action-btn mt-4 ${isAccepted ? "pulse-success" : ""}`}
                        variant={isAccepted ? "success" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setSelectedBulletId(bId);
                          setShowRewriteModal(true);
                        }}
                      >
                        {status === BulletStatus.PROPOSED
                          ? "Start Optimization →"
                          : isAccepted
                            ? "View Result"
                            : "Continue Tuning"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeBulletState && (
          <RewriteChatModal
            show={showRewriteModal}
            onHide={() => {
              setShowRewriteModal(false);
              setSelectedBulletId(null);
            }}
            bulletState={activeBulletState}
            sessionId={activeSession.session_id}
            service={service}
            onUpdate={handleBulletUpdate}
          />
        )}
      </div>
    );
  }

  // Session List View - Updated with color-coded cards
  return (
    <div className="rg-list-view">
      {/* Past sessions */}
      {sessions.length > 0 ? (
        <>
          <h3 className="rg-section-title">Previous Optimization Sessions</h3>
          <div className="rg-sessions-grid">
            {sessions.map((s) => {
              const isCompleted = s.status === OptimizeResumeStatus.COMPLETED;
              const isFailed = s.status === OptimizeResumeStatus.FAILED;
              const isPending = s.status === OptimizeResumeStatus.INITIATED;

              const statusClass = isCompleted
                ? "interview-hub-status-scored"
                : isFailed
                  ? "interview-hub-status-failed"
                  : "interview-hub-status-pending";

              const statusLabel = isCompleted
                ? "✅ Matched"
                : isFailed
                  ? "❌ Failed"
                  : "🕐 Processing";

              const actionLabel = isCompleted
                ? "View Report"
                : isFailed
                  ? "View Reason"
                  : "Processing…";

              const matchScore = isCompleted ? s.match_score || 0 : undefined;
              const cardStyles = getScoreStyles(matchScore);

              return (
                <div
                  key={s.session_id}
                  className="rg-session-card"
                  style={{
                    backgroundColor: cardStyles.backgroundColor,
                    border: cardStyles.border,
                    cursor: "pointer",
                  }}
                  onClick={() => handleViewSession(s)}
                >
                  {/* Left column: JD and date */}
                  <div className="rg-card-left">
                    <div className="rg-card-title" title={s.job_description}>
                      🎯 {truncateText(s.job_description, 35)}
                    </div>
                    <div className="rg-card-date">
                      Analyzed: {formatDateTime(s.created_at)}
                    </div>
                    {isCompleted && s.total_modifications > 0 && (
                      <div
                        className="rg-card-progress"
                        style={{
                          fontSize: "0.7rem",
                          color:
                            cardStyles.border === "1px solid #24C782"
                              ? "#059669"
                              : cardStyles.border === "1px solid #FFCA64"
                                ? "#D97706"
                                : cardStyles.border === "1px solid #E84C3D"
                                  ? "#DC2626"
                                  : "#6b7280",
                          marginTop: "0.25rem",
                        }}
                      >
                        Refinement: {s.completed_modifications}/
                        {s.total_modifications} bullets
                      </div>
                    )}
                  </div>

                  {/* Right column: match score and button */}
                  <div className="rg-card-right">
                    {isCompleted && (
                      <div
                        className="rg-card-score"
                        style={{
                          color:
                            cardStyles.border === "1px solid #24C782"
                              ? "#059669"
                              : cardStyles.border === "1px solid #FFCA64"
                                ? "#D97706"
                                : cardStyles.border === "1px solid #E84C3D"
                                  ? "#DC2626"
                                  : "#1f2937",
                        }}
                      >
                        {matchScore}%
                      </div>
                    )}

                    <button
                      className={`interview-hub-action-btn ${isPending ? "interview-hub-action-btn-muted" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewSession(s);
                      }}
                      disabled={isPending}
                    >
                      {actionLabel}
                    </button>
                  </div>

                  {/* Status badge at bottom */}
                  <div className="rg-card-status">
                    <span className={`interview-hub-status ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div
          className="interview-hub-empty-state"
          style={{ marginTop: "2rem" }}
        >
          <h3>No Optimization Sessions Yet</h3>
          <p style={{ textAlign: "center" }}>
            Upload your resume and a JD to get AI-powered bullet points and
            match analysis.
          </p>
        </div>
      )}

      {/* Optimization Init Modal - Controlled by parent */}
      <OptimizationInitModal
        show={showInitModal}
        onHide={() => setShowInitModal?.(false)}
        onStart={handleStartOptimization}
      />
    </div>
  );
};

export default ResumeAnalyzingTab;
